import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/how-it-works", label: "How It Works", id: "how-it-works" },
  { to: "/services", label: "Services", id: "services" },
  { to: "/corporate-stays", label: "Corporate Stays", id: "corporate-stays" },
  { to: "/about", label: "About", id: "about" },
  { to: "/faqs", label: "FAQs", id: "faqs" },
  { to: "/stay", label: "Stay With Us", id: "stay" },
];

function Wordmark({ light = true }) {
  return (
    <span className="flex items-center gap-3">
      <img
        src="/logo-square.png"
        alt="FieldGreen Property Management"
        className="h-10 w-10 rounded-md object-cover"
      />
      <span className="flex flex-col leading-tight">
        <span className={`font-serif text-lg font-semibold tracking-wide ${light ? "text-cream" : "text-forest"}`}>
          FieldGreen
        </span>
        <span className="text-[8px] font-semibold uppercase tracking-[0.32em] text-sage">Property Management</span>
      </span>
    </span>
  );
}

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-500 ${
        scrolled || open
          ? "bg-forest/90 backdrop-blur-xl border-b border-white/10 shadow-[0_8px_40px_rgba(10,27,20,0.35)]"
          : "bg-forest/60 backdrop-blur-md border-b border-white/5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-[76px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" aria-label="FieldGreen home">
          <Wordmark />
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.id}-link`}
              className={`link-underline text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                pathname === l.to ? "text-cream" : "text-cream/60 hover:text-cream"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/assessment"
            data-testid="nav-assessment-cta"
            className="hidden sm:inline-flex group items-center gap-2 rounded-full bg-cream px-5 py-2.5 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-sage hover:text-forest-900"
          >
            Get a Property Assessment
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="xl:hidden flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-cream"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden bg-forest/95 backdrop-blur-xl border-t border-white/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.35 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`nav-mobile-${l.id}-link`}
                    className={`font-serif text-3xl ${pathname === l.to ? "text-cream" : "text-cream/60"}`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                <Link
                  to="/assessment"
                  data-testid="nav-mobile-assessment-cta"
                  className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cream px-6 py-4 text-base font-semibold text-forest"
                >
                  Get a Property Assessment
                  <ArrowUpRight className="h-5 w-5" />
                </Link>
              </motion.div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
